import Portal from "elements/Portal";
import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { WEBSITE_BUILDER_PORTAL } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import { websiteBuilderZLayers } from "pages/secure/facility/websiteBuilder/editor/websiteBuilderZLayers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/pro-regular-svg-icons";
import { TWebsiteBuilderTreeRefMap } from "pages/secure/facility/websiteBuilder/editor/hooks/treeRefMap/useTreeRefMapRegistration";
import { WEBSITE_BUILDER_ROOT_ID } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderRoot";

export interface IWebsiteBuilderPopupContent {
  heading: string;
  render: () => ReactNode;
}

interface IWebsiteBuilderPopupProps {
  domRect: DOMRect;
  popupContent: IWebsiteBuilderPopupContent;
  treeRefMap: TWebsiteBuilderTreeRefMap;
  closePopup: () => void;
}

interface IWebsiteBuilderPopupPosition {
  x: number;
  y: number;
}

const POPUP_WIDTH = 350;
const POPUP_MARGIN = 20;

export function WebsiteBuilderPopup(props: IWebsiteBuilderPopupProps) {
  const { domRect, popupContent, treeRefMap, closePopup } = props;

  const popupRef = useRef<HTMLDivElement>(null);

  const [popupHeight, setPopupHeight] = useState<number>(null);
  const [popupPosition, setPopupPosition] = useState<IWebsiteBuilderPopupPosition>(null);
  const [rootDomRect, setRootDomRect] = useState<DOMRect>(null);

  useEffect(() => {
    const rootDomRect = treeRefMap.current.get(WEBSITE_BUILDER_ROOT_ID)?.getBoundingClientRect();

    setRootDomRect(rootDomRect);
  }, []);

  const handleKeydown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      closePopup();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);

    return () => document.removeEventListener("keydown", handleKeydown);
  }, [handleKeydown]);

  useEffect(() => {
    let resizeObserver: ResizeObserver = null;

    if (!popupRef.current) {
      return;
    }

    const update = () => {
      if (popupRef.current) {
        setPopupHeight(popupRef.current.getBoundingClientRect().height);
      }
    };

    update();

    resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(popupRef.current);

    return () => resizeObserver?.disconnect();
  }, []);

  useEffect(() => {
    const OFFSET = 10;

    if (popupHeight == null || !domRect || !rootDomRect) {
      return;
    }

    let y = domRect.y;

    const upperBound = window.innerHeight - OFFSET;

    if (y + popupHeight > upperBound) {
      y = y + domRect.height - popupHeight;
    }

    const rightX = domRect.right + POPUP_MARGIN;
    const leftX = domRect.left - POPUP_MARGIN - POPUP_WIDTH;
    const rightBoundaryX = domRect.right - POPUP_MARGIN - POPUP_WIDTH;

    let updatedPopupPosition: IWebsiteBuilderPopupPosition = null;

    if (rightX + POPUP_WIDTH < rootDomRect.right) {
      updatedPopupPosition = {
        x: rightX,
        y,
      };
    } else if (leftX > rootDomRect.left) {
      updatedPopupPosition = {
        x: leftX,
        y,
      };
    } else {
      updatedPopupPosition = {
        x: rightBoundaryX,
        y,
      };
    }

    setPopupPosition(updatedPopupPosition);
  }, [popupHeight, domRect, rootDomRect]);

  return (
    <Portal isMounted={true} parentId={WEBSITE_BUILDER_PORTAL}>
      <div
        ref={popupRef}
        className="website-builder-popup"
        style={{
          visibility: popupPosition ? "visible" : "hidden",
          left: popupPosition ? popupPosition.x : 0,
          top: popupPosition ? popupPosition.y : 0,
          width: POPUP_WIDTH,
          zIndex: websiteBuilderZLayers.portal,
        }}
        onMouseOver={e => e.stopPropagation()}
        onMouseOut={e => e.stopPropagation()}
        onDoubleClick={e => e.stopPropagation()}
        onPointerDown={e => e.stopPropagation()}
        onContextMenu={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {popupContent && (
          <div className="popup-content">
            <div className="popup-header">
              <h3>{popupContent.heading}</h3>
              <FontAwesomeIcon className="popup-header-close-icon" icon={faClose} onClick={closePopup} />
            </div>
            {popupContent.render()}
          </div>
        )}
      </div>
    </Portal>
  );
}
